import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { API, axios } from "@/api";
import { BrandLogo } from "@/components/shared/BrandLogo";
import { EaseStars } from "@/components/shared/EaseStars";
import { UnifiedStatusBadge } from "@/components/shared/UnifiedStatusBadge";
import { useUnifiedStatus } from "@/contexts/UnifiedStatusContext";
import { BottomNav } from "@/components/shared/Navigation";
import {
  ArrowLeft, Leaf, ShieldCheck, Heart, Gift, Star,
  ChevronRight, Loader2, Filter,
} from "lucide-react";
import { trackPageView } from "@/lib/track";

const ETHICAL_FILTERS = [
  { key: "all", label: "All Ethical", icon: Leaf },
  { key: "halal", label: "Halal", icon: Star },
  { key: "vegan", label: "Vegan", icon: Leaf },
  { key: "no_boycott", label: "No Boycott", icon: ShieldCheck },
  { key: "true_freebie", label: "True Freebie", icon: Gift },
  { key: "verified", label: "Verified", icon: ShieldCheck },
];

const EthicalCard = ({ offer, onClick, dealStatus }) => (
  <button onClick={onClick} className="w-full flex items-center gap-3 p-3.5 rounded-xl bg-white border border-emerald-100 shadow-sm hover:shadow-md hover:border-emerald-200 transition-all text-left" data-testid={`ethical-card-${offer.business_id}`}>
    <BrandLogo name={offer.name} logoUrl={offer.logo_url} size="sm" />
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-1.5">
        <p className="text-sm font-semibold text-slate-800 truncate">{offer.name}</p>
        {dealStatus && <UnifiedStatusBadge status={dealStatus} />}
      </div>
      <p className="text-xs text-slate-400 truncate">{offer.offer_title || offer.category}</p>
      <div className="flex flex-wrap gap-1 mt-1.5">
        {offer.halal_match && <span className="px-1.5 py-0.5 bg-green-50 text-green-600 border border-green-200 rounded text-[9px] font-semibold">{offer.dietary?.halal_status === "certified" ? "Halal Certified" : "Halal Friendly"}</span>}
        {offer.vegan_match && <span className="px-1.5 py-0.5 bg-lime-50 text-lime-600 border border-lime-200 rounded text-[9px] font-semibold">{offer.dietary?.vegan_status === "fully_vegan" ? "Vegan" : "Vegan Options"}</span>}
        {offer.is_true_freebie && <span className="px-1.5 py-0.5 bg-teal-50 text-teal-600 border border-teal-200 rounded text-[9px] font-semibold">True Freebie</span>}
        {offer.no_boycott && <span className="px-1.5 py-0.5 bg-sky-50 text-sky-600 border border-sky-200 rounded text-[9px] font-semibold">Clean</span>}
        {offer.is_verified && <span className="px-1.5 py-0.5 bg-indigo-50 text-indigo-600 border border-indigo-200 rounded text-[9px] font-semibold">Verified</span>}
      </div>
    </div>
    <div className="text-right flex-shrink-0">
      {offer.offer_value_est > 0 && <p className="text-sm font-bold text-green-600">${offer.offer_value_est}</p>}
      <EaseStars stars={offer.ease_stars} />
    </div>
    <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
  </button>
);

export default function EthicalPicksPage() {
  const navigate = useNavigate();
  const { getStatus } = useUnifiedStatus();
  const { user } = useAuth();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("all");

  useEffect(() => {
    trackPageView("ethical_picks");
    (async () => {
      try {
        const res = await axios.get(`${API}/offers?limit=500`);
        const all = Array.isArray(res.data) ? res.data : res.data.offers || [];
        // Tag ethical attributes
        const tagged = all.map(o => ({
          ...o,
          halal_match: o.dietary?.halal_status === "certified" || o.dietary?.halal_status === "friendly",
          vegan_match: o.dietary?.vegan_status === "fully_vegan" || o.dietary?.vegan_status === "friendly",
          no_boycott: !o.boycott_flag,
          is_verified: (o.crowd_verification_count || 0) > 0,
        })).filter(o => o.halal_match || o.vegan_match || o.is_true_freebie || o.no_boycott);
        setOffers(tagged);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = activeFilter === "all" ? offers
    : activeFilter === "halal" ? offers.filter(o => o.halal_match)
    : activeFilter === "vegan" ? offers.filter(o => o.vegan_match)
    : activeFilter === "no_boycott" ? offers.filter(o => o.no_boycott)
    : activeFilter === "true_freebie" ? offers.filter(o => o.is_true_freebie)
    : activeFilter === "verified" ? offers.filter(o => o.is_verified)
    : offers;

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>;

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: "var(--theme-page-bg, #ffffff)" }} data-testid="ethical-picks-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-600 text-white px-5 pt-5 pb-8">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate(-1)} className="p-1.5 hover:bg-white/10 rounded-lg" data-testid="ethical-back"><ArrowLeft className="w-5 h-5" /></button>
          <Leaf className="w-5 h-5" />
          <h1 className="text-base font-bold">Ethical Picks</h1>
        </div>
        <p className="text-emerald-100 text-sm leading-relaxed">
          Curated rewards that align with your values — halal, vegan, boycott-free, and no strings attached.
        </p>
        <div className="grid grid-cols-3 gap-2 mt-4">
          <div className="bg-white/10 backdrop-blur rounded-xl p-2.5 text-center">
            <p className="text-lg font-bold">{offers.filter(o => o.halal_match).length}</p>
            <p className="text-[10px] text-emerald-200">Halal</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-2.5 text-center">
            <p className="text-lg font-bold">{offers.filter(o => o.vegan_match).length}</p>
            <p className="text-[10px] text-emerald-200">Vegan</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-2.5 text-center">
            <p className="text-lg font-bold">{offers.filter(o => o.is_true_freebie).length}</p>
            <p className="text-[10px] text-emerald-200">True Deals</p>
          </div>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="px-4 -mt-4">
        <div className="bg-white rounded-2xl shadow-md p-2 flex gap-1 overflow-x-auto">
          {ETHICAL_FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setActiveFilter(f.key)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all ${
                activeFilter === f.key
                  ? "bg-emerald-500 text-white shadow-sm"
                  : "text-slate-500 hover:bg-slate-50"
              }`}
              data-testid={`ethical-filter-${f.key}`}
            >
              <f.icon className="w-3.5 h-3.5" />
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Results */}
      <div className="px-4 mt-4 space-y-2">
        <p className="text-xs text-slate-400 font-medium px-1">{filtered.length} ethical picks</p>
        {filtered.slice(0, 30).map(o => (
          <EthicalCard key={o.business_id} offer={o} onClick={() => navigate(`/offer/${o.business_id}`)} dealStatus={getStatus(o.business_id)} />
        ))}
        {filtered.length === 0 && (
          <div className="text-center py-12">
            <Leaf className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-400 font-medium">No matches for this filter</p>
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
